import { useAppResumeTimer } from "@/composables/useAppResumeTimer/useAppResumeTimer";

/**
 * LGSAM-469: detects when the app returns to the foreground after being backgrounded/asleep
 * for more than 1 hour and, if so, forces a full app reload so it comes back up on the Home
 * Screen exactly as if freshly launched (screens reset, session revalidated, data re-fetched
 * via the normal cold-start flow - AC #3, #4, #6, #7, #8). A plain reload is enough: on every
 * boot `plugins/redirectToPath/index.ts` already forces the route back to root whenever it
 * isn't already there, and `plugins/startup/index.ts` then redirects root to `mainPage`.
 *
 * Detection is based on the standard `document.visibilitychange` API (`document.hidden`),
 * which LG webOS and Samsung Tizen support the same way any Chromium-based browser does -
 * no platform-specific lifecycle event is required for this ticket's scope (AC #10).
 */
export default defineNuxtPlugin(() => {
    if (typeof document === 'undefined') {
        return;
    }

    const { recordBackgrounded, consumeAwayDurationExceedsThreshold } = useAppResumeTimer();

    const onVisibilityChange = (): void => {
        if (document.hidden) {
            recordBackgrounded();
            return;
        }

        if (consumeAwayDurationExceedsThreshold()) {
            window.location.reload();
        }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
});
